import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Droplets, Camera, AlertTriangle, History, Plus, X, SlidersHorizontal, Minus, KeyRound, Download, CalendarDays } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { enqueue, isOffline } from "@/lib/offline";
import { useAuth } from "@/context/AuthContext";
import ProductoBuscador from "@/components/ProductoBuscador";
import InsumoBuscador from "@/components/InsumoBuscador";
import HistorialMermas from "@/components/mermas/HistorialMermas";

const MOTIVOS = ["Derrame en mezclado", "Rotura de envase", "Evaporación", "Vencimiento", "Otro"];
const ROLES_ERP = ["gran_admin", "coordinador", "contabilidad"];

function Label({ children }) {
  return <label className="eyebrow block">{children}</label>;
}

export default function Mermas() {
  const { user } = useAuth();
  const esErp = ROLES_ERP.includes(user?.rol);
  const [modo, setModo] = useState("merma");
  const [sucursales, setSucursales] = useState([]);
  const [sucursalId, setSucursalId] = useState("");
  const [componentes, setComponentes] = useState([]);
  const [prodSel, setProdSel] = useState(null);
  const [form, setForm] = useState({ tipo_item: "componente", item_id: "", cantidad: "", motivo: MOTIVOS[0], detalle: "", foto_data: null });
  const [ajuste, setAjuste] = useState({ componente_id: "", direccion: "quitar", cantidad: "", observacion: "", pin: "" });
  const [saving, setSaving] = useState(false);
  const [refresco, setRefresco] = useState(0);

  useEffect(() => {
    if (!esErp) return;
    api.get("/sucursales").then((r) => {
      const activas = r.data.filter((s) => s.activa !== false);
      setSucursales(activas);
      const alm = activas.find((s) => ["almacen", "cedis"].includes(s.tipo) && (!user?.cedis_id || s.id === user.cedis_id)) || activas[0];
      setSucursalId((v) => v || alm?.id || "");
    }).catch(() => {});
  }, [esErp, user]);

  const cargarComponentes = useCallback(async () => {
    try {
      const r = await api.get("/componentes", { params: esErp && sucursalId ? { sucursal_id: sucursalId } : {} });
      setComponentes(r.data);
    } catch {}
  }, [esErp, sucursalId]);

  useEffect(() => { cargarComponentes(); }, [cargarComponentes]);

  const itemSel = form.tipo_item === "componente" ? componentes.find((x) => x.id === form.item_id) : prodSel;
  const unidad = form.tipo_item === "componente" ? itemSel?.unidad || "ml" : "pzas";
  const compAj = componentes.find((x) => x.id === ajuste.componente_id);
  const resultado = compAj ? Math.round(((compAj.stock || 0) + (ajuste.direccion === "agregar" ? 1 : -1) * (parseFloat(ajuste.cantidad) || 0)) * 1000) / 1000 : null;

  function onFoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, foto_data: reader.result }));
    reader.readAsDataURL(file);
  }

  function despues() {
    setRefresco((n) => n + 1);
    cargarComponentes();
  }

  async function registrarMerma(e) {
    e.preventDefault();
    const cantidad = parseFloat(form.cantidad);
    if (!form.item_id || !cantidad || cantidad <= 0) return toast.error("Completa producto/componente y cantidad");
    setSaving(true);
    const payload = {
      tipo_item: form.tipo_item, item_id: form.item_id, cantidad, unidad,
      motivo: form.detalle ? `${form.motivo}: ${form.detalle}` : form.motivo,
      foto_data: form.foto_data, offline_uuid: crypto.randomUUID(), sucursal_id: esErp ? sucursalId || null : null,
    };
    try {
      if (isOffline()) {
        enqueue("merma", payload);
        toast.success("Merma en cola: se sincronizará al recuperar conexión");
      } else {
        await api.post("/mermas", payload);
        toast.success("Merma registrada con trazabilidad");
      }
      setForm({ tipo_item: "componente", item_id: "", cantidad: "", motivo: MOTIVOS[0], detalle: "", foto_data: null });
      setProdSel(null);
      despues();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function registrarAjuste(e) {
    e.preventDefault();
    const cantidad = parseFloat(ajuste.cantidad);
    if (!ajuste.componente_id || !cantidad || cantidad <= 0) return toast.error("Selecciona el insumo y la cantidad");
    if (ajuste.observacion.trim().length < 5) return toast.error("Describe la observación del ajuste");
    if (!ajuste.pin) return toast.error("Captura el PIN de autorización");
    setSaving(true);
    try {
      const r = await api.post("/ajustes/inventario", { ...ajuste, cantidad, sucursal_id: esErp ? sucursalId || null : null });
      toast.success(`Ajuste aplicado: ${r.data.componente_nombre} ${r.data.stock_anterior} → ${r.data.stock_nuevo} ${r.data.unidad}`);
      setAjuste({ componente_id: "", direccion: "quitar", cantidad: "", observacion: "", pin: "" });
      despues();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto animate-fade-up" data-testid="mermas-page">
      <div className="mb-8">
        <div className="eyebrow mb-1">Control de inventario</div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-brand-ink">Merma / Ajuste de stock</h1>
        <p className="text-sm text-brand-muted mt-1">Registra mermas con evidencia o corrige descuadres de stock (agregar / quitar) con PIN de autorización. Todo queda en el histórico y en el reporte mensual.</p>
      </div>

      <div className="grid grid-cols-12 gap-6 lg:gap-8">
        <div className="col-span-12 lg:col-span-4 space-y-3 h-fit">
          <div className="flex gap-1 bg-brand-surface p-1 rounded-full">
            <button type="button" data-testid="modo-merma" onClick={() => setModo("merma")} className={`flex-1 h-9 rounded-full text-sm font-semibold transition-colors duration-150 flex items-center justify-center gap-1.5 ${modo === "merma" ? "bg-brand-ink text-white" : "text-brand-slate hover:text-brand-ink"}`}><AlertTriangle className="w-3.5 h-3.5" /> Merma</button>
            <button type="button" data-testid="modo-ajuste" onClick={() => setModo("ajuste")} className={`flex-1 h-9 rounded-full text-sm font-semibold transition-colors duration-150 flex items-center justify-center gap-1.5 ${modo === "ajuste" ? "bg-brand-ink text-white" : "text-brand-slate hover:text-brand-ink"}`}><SlidersHorizontal className="w-3.5 h-3.5" /> Ajuste de stock</button>
          </div>

          {esErp && (
            <div className="surface-card p-4">
              <Label>Sucursal / almacén</Label>
              <select data-testid="mermas-sucursal-select" value={sucursalId} onChange={(e) => { setSucursalId(e.target.value); setForm((f) => ({ ...f, item_id: "" })); setAjuste((a) => ({ ...a, componente_id: "" })); }} className="field mt-1">
                {sucursales.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
              </select>
            </div>
          )}

          {modo === "merma" ? (
            <form onSubmit={registrarMerma} className="surface-card p-5 space-y-4" data-testid="merma-form">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-rose-50 border border-rose-200 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-rose-600" /></div>
                <h2 className="font-display text-lg font-bold text-brand-ink">Registrar merma</h2>
              </div>
              <div>
                <Label>Tipo</Label>
                <div className="flex gap-1 mt-1 bg-brand-surface p-1 rounded-full">
                  {["componente", "producto"].map((t) => (
                    <button key={t} type="button" data-testid={`merma-tipo-${t}`} onClick={() => { setForm({ ...form, tipo_item: t, item_id: "" }); setProdSel(null); }} className={`flex-1 h-8 rounded-full text-sm font-semibold capitalize transition-colors duration-150 ${form.tipo_item === t ? "bg-brand-ink text-white" : "text-brand-slate hover:text-brand-ink"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>{form.tipo_item === "componente" ? "Componente" : "Producto"}</Label>
                {form.tipo_item === "componente" ? (
                  <InsumoBuscador testId="merma-item-select" className="mt-1" componentes={componentes} value={form.item_id} permitirVacio={false} onSelect={(c) => setForm({ ...form, item_id: c?.id || "" })} placeholder="Busca el insumo por nombre o SKU…" />
                ) : prodSel ? (
                  <div className="mt-1 flex items-center gap-2 rounded-xl border border-brand-amber bg-brand-amber-soft/40 px-3 py-2" data-testid="merma-producto-sel"><div className="flex-1 min-w-0"><div className="text-sm font-semibold text-brand-ink truncate">{prodSel.nombre}</div><div className="font-mono text-[10px] uppercase text-brand-muted">{prodSel.sku} · {prodSel.presentacion}</div></div><button type="button" data-testid="merma-producto-quitar" onClick={() => { setProdSel(null); setForm({ ...form, item_id: "" }); }} className="p-1.5 text-brand-muted hover:text-rose-600"><X className="w-4 h-4" /></button></div>
                ) : (
                  <ProductoBuscador testId="merma-producto-buscador" className="mt-1" onSelect={(p) => { setProdSel(p); setForm({ ...form, item_id: p.id }); }} placeholder="Busca el PACK por SKU o nombre…" renderExtra={(p) => <span className="chip chip-emerald">{p.unidades_posibles} disp.</span>} />
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Cantidad ({unidad})</Label>
                  <input data-testid="merma-quantity-input" type="number" min="0" step="0.001" value={form.cantidad} onChange={(e) => setForm({ ...form, cantidad: e.target.value })} className="field mt-1 font-mono" required />
                </div>
                <div>
                  <Label>Motivo</Label>
                  <select data-testid="merma-reason-select" value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} className="field mt-1">
                    {MOTIVOS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Descripción del motivo</Label>
                <input data-testid="merma-detail-input" value={form.detalle} onChange={(e) => setForm({ ...form, detalle: e.target.value })} placeholder="Detalle opcional" className="field mt-1" />
              </div>
              <div>
                <label className="eyebrow flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Foto de evidencia</label>
                <input data-testid="merma-photo-input" type="file" accept="image/*" onChange={onFoto} className="mt-1 text-xs text-brand-muted file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-brand-surface file:text-brand-graphite file:text-xs file:font-semibold hover:file:bg-brand-line" />
                {form.foto_data && <img src={form.foto_data} alt="evidencia" className="mt-2 h-20 rounded-xl border border-brand-line object-cover animate-pop" data-testid="merma-photo-preview" />}
              </div>
              <button data-testid="merma-submit-button" type="submit" disabled={saving} className="btn-amber w-full h-11">
                <Plus className="w-4 h-4" /> {saving ? "Registrando…" : "Registrar merma"}
              </button>
            </form>
          ) : (
            <form onSubmit={registrarAjuste} className="surface-card p-5 space-y-4" data-testid="ajuste-form">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center"><SlidersHorizontal className="w-4 h-4 text-amber-600" /></div>
                <div>
                  <h2 className="font-display text-lg font-bold text-brand-ink">Ajuste de stock</h2>
                  <p className="text-[11px] text-brand-muted">Para descuadres: corrige la existencia real del insumo.</p>
                </div>
              </div>
              <div>
                <Label>Insumo</Label>
                <InsumoBuscador testId="ajuste-item-select" className="mt-1" componentes={componentes} value={ajuste.componente_id} permitirVacio={false} onSelect={(c) => setAjuste({ ...ajuste, componente_id: c?.id || "" })} placeholder="Busca el insumo por nombre o SKU…" />
              </div>
              <div>
                <Label>Operación</Label>
                <div className="flex gap-1 mt-1 bg-brand-surface p-1 rounded-full">
                  <button type="button" data-testid="ajuste-dir-agregar" onClick={() => setAjuste({ ...ajuste, direccion: "agregar" })} className={`flex-1 h-8 rounded-full text-sm font-semibold flex items-center justify-center gap-1 transition-colors duration-150 ${ajuste.direccion === "agregar" ? "bg-emerald-600 text-white" : "text-brand-slate hover:text-brand-ink"}`}><Plus className="w-3.5 h-3.5" /> Agregar</button>
                  <button type="button" data-testid="ajuste-dir-quitar" onClick={() => setAjuste({ ...ajuste, direccion: "quitar" })} className={`flex-1 h-8 rounded-full text-sm font-semibold flex items-center justify-center gap-1 transition-colors duration-150 ${ajuste.direccion === "quitar" ? "bg-rose-600 text-white" : "text-brand-slate hover:text-brand-ink"}`}><Minus className="w-3.5 h-3.5" /> Quitar</button>
                </div>
              </div>
              <div>
                <Label>Cantidad {compAj ? `(${compAj.unidad})` : ""}</Label>
                <input data-testid="ajuste-quantity-input" type="number" min="0" step="0.001" value={ajuste.cantidad} onChange={(e) => setAjuste({ ...ajuste, cantidad: e.target.value })} className="field mt-1 font-mono" required />
                {compAj && (
                  <div className="mt-1.5 text-xs text-brand-slate font-mono" data-testid="ajuste-preview">
                    Stock actual <b>{compAj.stock}</b> → nuevo <b className={resultado < 0 ? "text-rose-600" : "text-emerald-700"}>{resultado}</b> {compAj.unidad}
                  </div>
                )}
              </div>
              <div>
                <Label>Observación (obligatoria)</Label>
                <textarea data-testid="ajuste-observacion-input" value={ajuste.observacion} onChange={(e) => setAjuste({ ...ajuste, observacion: e.target.value })} placeholder="Ej. Conteo físico 12/06: sobraban 3 piezas capturadas doble en recepción" className="field mt-1 min-h-[72px]" required />
              </div>
              <div>
                <label className="eyebrow flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> PIN de autorización (gerente / admin)</label>
                <input data-testid="ajuste-pin-input" type="password" inputMode="numeric" maxLength={6} value={ajuste.pin} onChange={(e) => setAjuste({ ...ajuste, pin: e.target.value.replace(/\D/g, "") })} className="field mt-1 font-mono tracking-[0.4em]" placeholder="••••" required />
              </div>
              <button data-testid="ajuste-submit-button" type="submit" disabled={saving || resultado < 0} className={`w-full h-11 ${ajuste.direccion === "agregar" ? "btn-amber" : "btn-ink"}`}>
                {ajuste.direccion === "agregar" ? <Plus className="w-4 h-4" /> : <Minus className="w-4 h-4" />} {saving ? "Aplicando…" : ajuste.direccion === "agregar" ? "Agregar al stock" : "Quitar del stock"}
              </button>
            </form>
          )}
        </div>

        <div className="col-span-12 lg:col-span-8">
          <HistorialMermas refresco={refresco} sucursales={esErp ? sucursales : []} />
        </div>
      </div>
    </div>
  );
}
